import { z } from 'zod'
import { moneySchema, optionalText, versionSchema } from './common.js'
import type { ModuleKey } from './permissions.js'

export const accountTypeSchema = z.enum(['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'])
export type AccountType = z.infer<typeof accountTypeSchema>

/** Debit-normal types increase with debits; the rest increase with credits. */
export const DEBIT_NORMAL: ReadonlySet<AccountType> = new Set(['ASSET', 'EXPENSE'])

/**
 * Accounting roles: postings name a role, the tenant maps each role to one of its accounts.
 * `defaultCode` is the seeded chart's code (kept compatible with the legacy numbering); `type` is the
 * account type a mapped account must have (contra accounts keep their section's type).
 */
export const ACCOUNT_ROLES = {
  cash: { en: 'Cash on hand', ar: 'النقدية', defaultCode: '1000', type: 'ASSET' },
  bank: { en: 'Bank', ar: 'البنك', defaultCode: '1100', type: 'ASSET' },
  input_vat: {
    en: 'VAT recoverable (input)',
    ar: 'ضريبة القيمة المضافة المستردة',
    defaultCode: '1150',
    type: 'ASSET',
  },
  receivables: { en: 'Accounts receivable', ar: 'الذمم المدينة', defaultCode: '1200', type: 'ASSET' },
  /** Cheques received and not yet paid by the bank (ADR-046): post-dated, in hand or deposited. */
  cheques_receivable: {
    en: 'Cheques under collection',
    ar: 'شيكات برسم التحصيل',
    defaultCode: '1230',
    type: 'ASSET',
  },
  /** Contracting (projects/04 §9.1): retention the client holds back, a contract asset until released. */
  retention_receivable: {
    en: 'Retention receivable',
    ar: 'محتجزات لدى العملاء',
    defaultCode: '1210',
    type: 'ASSET',
  },
  inventory: { en: 'Inventory', ar: 'المخزون', defaultCode: '1300', type: 'ASSET' },
  payables: { en: 'Accounts payable', ar: 'الذمم الدائنة', defaultCode: '2000', type: 'LIABILITY' },
  output_vat: {
    en: 'VAT payable (output)',
    ar: 'ضريبة القيمة المضافة المستحقة',
    defaultCode: '2100',
    type: 'LIABILITY',
  },
  customer_credits: {
    en: 'Customer credits',
    ar: 'أرصدة العملاء الدائنة',
    defaultCode: '2210',
    type: 'LIABILITY',
  },
  /** Cheques written and not yet paid out by the bank (ADR-046). */
  cheques_payable: {
    en: 'Cheques issued, not presented',
    ar: 'شيكات صادرة برسم الدفع',
    defaultCode: '2230',
    type: 'LIABILITY',
  },
  /** Contracting: advance payments invoiced to clients, recovered from later certificates. */
  customer_advances: {
    en: 'Customer advances',
    ar: 'دفعات مقدمة من العملاء',
    defaultCode: '2220',
    type: 'LIABILITY',
  },
  /** Payroll (ADR-048): net pay owed to employees from a run's approval until it is paid. */
  salaries_payable: {
    en: 'Salaries payable',
    ar: 'رواتب مستحقة الدفع',
    defaultCode: '2310',
    type: 'LIABILITY',
  },
  /** Payroll: both shares of the social contributions, owed to the Social Protection Fund. */
  social_insurance_payable: {
    en: 'Social Protection Fund payable',
    ar: 'اشتراكات صندوق الحماية الاجتماعية المستحقة',
    defaultCode: '2320',
    type: 'LIABILITY',
  },
  /** Commission (ADR-049): approved commission not yet paid (by payroll or from a cash or bank account). */
  commissions_payable: {
    en: 'Commissions payable',
    ar: 'عمولات مستحقة الدفع',
    defaultCode: '2350',
    type: 'LIABILITY',
  },
  /** Payroll: end-of-service gratuity accrued for employees still in service. */
  end_of_service_provision: {
    en: 'End-of-service provision',
    ar: 'مخصص مكافأة نهاية الخدمة',
    defaultCode: '2330',
    type: 'LIABILITY',
  },
  /** Payroll: deductions withheld from pay for others (loans, advances, penalties) unless a component names an account. */
  payroll_deductions_payable: {
    en: 'Payroll deductions payable',
    ar: 'استقطاعات رواتب مستحقة',
    defaultCode: '2340',
    type: 'LIABILITY',
  },
  owner_equity: { en: "Owner's equity", ar: 'حقوق الملكية', defaultCode: '3000', type: 'EQUITY' },
  retained_earnings: { en: 'Retained earnings', ar: 'الأرباح المحتجزة', defaultCode: '3100', type: 'EQUITY' },
  opening_balance_equity: {
    en: 'Opening balance equity',
    ar: 'حقوق ملكية الأرصدة الافتتاحية',
    defaultCode: '3900',
    type: 'EQUITY',
  },
  sales_revenue: { en: 'Sales revenue', ar: 'إيرادات المبيعات', defaultCode: '4000', type: 'REVENUE' },
  sales_returns: { en: 'Sales returns', ar: 'مردودات المبيعات', defaultCode: '4100', type: 'REVENUE' },
  sales_allowances: { en: 'Sales allowances', ar: 'مسموحات المبيعات', defaultCode: '4150', type: 'REVENUE' },
  service_revenue: { en: 'Service revenue', ar: 'إيرادات الخدمات', defaultCode: '4200', type: 'REVENUE' },
  /** Contracting: revenue of certified progress claims (invoice net + the certificate journal). */
  contract_revenue: { en: 'Contract revenue', ar: 'إيرادات العقود', defaultCode: '4300', type: 'REVENUE' },
  other_income: { en: 'Other income', ar: 'إيرادات أخرى', defaultCode: '4900', type: 'REVENUE' },
  cogs: { en: 'Cost of goods sold', ar: 'تكلفة البضاعة المباعة', defaultCode: '5000', type: 'EXPENSE' },
  purchase_returns: {
    en: 'Purchase returns and discounts',
    ar: 'مردودات وخصومات المشتريات',
    defaultCode: '5200',
    type: 'EXPENSE',
  },
  stock_adjustment: { en: 'Stock adjustments', ar: 'تسويات المخزون', defaultCode: '5250', type: 'EXPENSE' },
  /**
   * Project costs (projects/02 §4): goods delivered to a project site, material issued to projects and
   * cost entries, when their cost category names no account of its own.
   */
  project_costs: { en: 'Project costs', ar: 'تكاليف المشاريع', defaultCode: '5300', type: 'EXPENSE' },
  /** Payroll: earnings (salaries, allowances, overtime) unless a component names its own account. */
  salaries_expense: { en: 'Salaries and wages', ar: 'الرواتب والأجور', defaultCode: '6100', type: 'EXPENSE' },
  /** Payroll: the employer's share of the social contributions. */
  employer_contribution_expense: {
    en: 'Social insurance (employer share)',
    ar: 'التأمينات الاجتماعية (حصة صاحب العمل)',
    defaultCode: '6110',
    type: 'EXPENSE',
  },
  /** Commission (ADR-049): sales commission and technician incentives of approved runs. */
  commission_expense: {
    en: 'Sales commissions',
    ar: 'عمولات المبيعات',
    defaultCode: '6130',
    type: 'EXPENSE',
  },
  /** Payroll: end-of-service gratuity expense (the provision's movement and settlement differences). */
  end_of_service_expense: {
    en: 'End-of-service benefits',
    ar: 'مكافآت نهاية الخدمة',
    defaultCode: '6120',
    type: 'EXPENSE',
  },
  operating_expense: {
    en: 'Operating expenses',
    ar: 'المصروفات التشغيلية',
    defaultCode: '6000',
    type: 'EXPENSE',
  },
  discount_given: { en: 'Discounts given', ar: 'الخصومات الممنوحة', defaultCode: '6200', type: 'EXPENSE' },
  shipping_expense: { en: 'Shipping expense', ar: 'مصروفات الشحن', defaultCode: '6900', type: 'EXPENSE' },
  rounding: { en: 'Rounding differences', ar: 'فروقات التقريب', defaultCode: '6950', type: 'EXPENSE' },
  /** Till count differences at shift close: a shortage is debited, a surplus credited. */
  cash_over_short: {
    en: 'Cash over and short',
    ar: 'زيادة وعجز الصندوق',
    defaultCode: '6960',
    type: 'EXPENSE',
  },
} as const satisfies Record<string, { en: string; ar: string; defaultCode: string; type: AccountType }>

export type AccountRole = keyof typeof ACCOUNT_ROLES

/**
 * Roles only an opt-in module posts to (ADR-048): an unmapped one matters only while its module is on (its
 * accounts are created on first use), so the account mapping's warning leaves it out otherwise.
 */
export const ACCOUNT_ROLE_MODULES: Partial<Record<AccountRole, ModuleKey>> = {
  salaries_expense: 'payroll',
  salaries_payable: 'payroll',
  social_insurance_payable: 'payroll',
  end_of_service_provision: 'payroll',
  payroll_deductions_payable: 'payroll',
  employer_contribution_expense: 'payroll',
  end_of_service_expense: 'payroll',
}
export const accountRoleSchema = z.enum(Object.keys(ACCOUNT_ROLES) as [AccountRole, ...AccountRole[]])

/**
 * The only roles a cash or bank account's own GL account may hold: mapped to any other role (receivables,
 * input VAT…), a document would post to the cash or bank account on both sides of its entry.
 */
export const TREASURY_ROLES: readonly AccountRole[] = ['cash', 'bank']

export const accountInputSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9.-]+$/, 'Use letters, digits, dots or dashes'),
  name: z.string().trim().min(1).max(200),
  nameAr: optionalText(200),
  type: accountTypeSchema,
  parentId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  isPostable: z.boolean().default(true),
})
export type AccountInput = z.input<typeof accountInputSchema>
export const accountUpdateSchema = accountInputSchema.extend({ version: versionSchema })
export type AccountUpdate = z.input<typeof accountUpdateSchema>

export interface Account {
  id: string
  code: string
  name: string
  nameAr: string | null
  type: AccountType
  parentId: string | null
  isPostable: boolean
  isSystem: boolean
  archived: boolean
  roles: AccountRole[]
  /**
   * The GL account of a cash or bank account (archived or not): only TREASURY_ROLES may be mapped to it.
   * The API always sends it; optional for fixtures.
   */
  treasury?: boolean
  version: number
}

/**
 * `GET /accounts/options?purpose=`: accounts a purchase service line (`purchase`) or an expense and
 * its category (`expense`) may be charged to. Both purposes currently apply the same rule as the
 * API's validation: active, postable EXPENSE or ASSET accounts that are neither a cash/bank account
 * nor a control account (inventory, receivables, payables, the cheque clearing accounts, contract
 * advances and retention, input/output VAT).
 */
export const accountOptionsQuerySchema = z.object({
  /**
   * `purchase` / `expense`: expense and asset accounts a purchase service line, an expense or its
   * category may be charged to. `bank_adjustment`: income and expense accounts for a bank statement
   * line found only on the statement (charges, interest). `project_category`: expense accounts a
   * project cost category may post to (not cost of sales, purchase returns or stock adjustments).
   * `project_credit`: income and expense accounts a project cost allocation may credit.
   * `labour_credit` (P6): liability and expense accounts a labour posting may credit (salaries payable, a
   * labour clearing account, a "labour absorbed" contra), never revenue. Never cash/bank or document-owned
   * accounts.
   */
  purpose: z.enum([
    'expense',
    'purchase',
    'bank_adjustment',
    'project_category',
    'project_credit',
    'labour_credit',
  ]),
})
export type AccountOptionsQuery = z.input<typeof accountOptionsQuerySchema>

export interface AccountOption {
  id: string
  code: string
  name: string
  nameAr: string | null
  type: AccountType
}

export const journalLineInputSchema = z
  .object({
    accountId: z.uuid(),
    debit: moneySchema.default('0'),
    credit: moneySchema.default('0'),
    description: optionalText(300),
  })
  .refine((l) => Number(l.debit) > 0 !== Number(l.credit) > 0, {
    message: 'Enter either a debit or a credit on each line',
    path: ['debit'],
  })

export const manualJournalSchema = z.object({
  entryDate: z.iso.date(),
  reference: optionalText(100),
  description: z.string().trim().min(1, 'Describe the entry').max(500),
  lines: z.array(journalLineInputSchema).min(2, 'An entry needs at least two lines').max(200),
})
export type ManualJournalInput = z.input<typeof manualJournalSchema>

export const reverseJournalSchema = z.object({
  entryDate: z.iso.date().optional(),
  reason: z.string().trim().min(1).max(300),
})
export type ReverseJournalInput = z.input<typeof reverseJournalSchema>

export type JournalKind = 'MANUAL' | 'SYSTEM' | 'REVERSAL' | 'CLOSING' | 'OPENING'

export interface JournalLine {
  lineNo: number
  accountId: string
  accountCode: string
  accountName: string
  debit: string
  credit: string
  description: string | null
  partyType: 'CUSTOMER' | 'SUPPLIER' | null
  partyId: string | null
}

export interface JournalEntry {
  id: string
  number: string
  entryDate: string
  kind: JournalKind
  sourceType: string | null
  sourceId: string | null
  reference: string | null
  description: string | null
  reversalOfId: string | null
  reversedById: string | null
  total: string
  postedAt: string
  lines: JournalLine[]
}

export const trialBalanceQuerySchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
})

export interface TrialBalanceRow {
  accountId: string
  code: string
  name: string
  nameAr: string | null
  type: AccountType
  openingDebit: string
  openingCredit: string
  periodDebit: string
  periodCredit: string
  closingDebit: string
  closingCredit: string
}

export interface TrialBalance {
  from: string
  to: string
  rows: TrialBalanceRow[]
  totals: { periodDebit: string; periodCredit: string; closingDebit: string; closingCredit: string }
}

export const accountRolesUpdateSchema = z.object({
  mappings: z.partialRecord(accountRoleSchema, z.uuid()),
})
export type AccountRolesUpdate = z.input<typeof accountRolesUpdateSchema>

export const fiscalYearInputSchema = z.object({
  name: z.string().trim().min(1).max(50),
  startDate: z.iso.date(),
  endDate: z.iso.date(),
})
export type FiscalYearInput = z.input<typeof fiscalYearInputSchema>

export interface FiscalYear {
  id: string
  name: string
  startDate: string
  endDate: string
  closed: boolean
  /** The year-end closing entry (null while open, or when there was nothing to close). */
  closingEntryId: string | null
}

// ── lock date (settings G-A2, Q6) ─────────────────────────────────────────────

/**
 * PUT /accounting/lock-date. Nothing dated on or before `lockDate` can be posted (any journal entry:
 * manual, system, reversal, opening; so no document posting or void either); null = no lock date.
 * It may not be after today, nor before the end of a closed fiscal year.
 */
export const lockDateUpdateSchema = z.object({
  lockDate: z.iso.date().nullable(),
  version: versionSchema,
})
export type LockDateUpdate = z.input<typeof lockDateUpdateSchema>

/** GET and PUT /accounting/lock-date. Never set: `lockDate` null at version 1. */
export interface LockDateState {
  lockDate: string | null
  version: number
  updatedAt: string | null
  updatedBy: string | null
  /**
   * The last day of the latest closed fiscal year (null when none is closed): the lock date cannot be
   * set before it, nor cleared while a year is closed.
   */
  closedThrough: string | null
}

export const journalListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  kind: z.enum(['MANUAL', 'SYSTEM', 'REVERSAL', 'CLOSING', 'OPENING']).optional(),
  accountId: z.uuid().optional(),
})
export type JournalListQuery = z.input<typeof journalListQuerySchema>
export type JournalEntrySummary = Omit<JournalEntry, 'lines'>

/** `GET /journal-entries/summary`: the list's filters other than `kind`, and no paging. */
export const journalSummaryQuerySchema = journalListQuerySchema.pick({
  from: true,
  to: true,
  accountId: true,
})
export type JournalSummaryQuery = z.input<typeof journalSummaryQuerySchema>

/** One calendar month (`YYYY-MM`) of journal entries; `total` is the sum of their debits. */
export interface JournalMonth {
  month: string
  count: number
  total: string
}

/**
 * The journal list's figures for the current filters. `all.count` and each `kinds[k].count` equal the
 * `total` of the list call without / with `kind=k`; `total` sums those entries' `total` (debits).
 * `months`: entries of the last 6 calendar months (this one last, empty months included), following
 * the account filter but not the dates. As of `asOf`, today in Asia/Muscat.
 */
export interface JournalListSummary {
  asOf: string
  all: { count: number; total: string }
  kinds: Record<JournalKind, { count: number; total: string }>
  months: JournalMonth[]
}
